import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import DataTable from "react-data-table-component";
import ItemService from "../../../../services/supplier_appCommonService";
import CreateRFQSuppliers from "../../../../Components/Modal/supplier_app/Sourcing/CreateRFQSuppliers";

const ManageSuppliersModal = ({ show, onHide, formInputs, onSuccess }) => {
  const [supplierList, setSupplierList] = useState([]);
  const [getSupplierModal, setSupplierModal] = useState(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && formInputs?.supplierList) {
      setSupplierList([...formInputs.supplierList]);
    }
  }, [show, formInputs]);

  const isAcknowledged = (supplier) =>
    supplier?.rfxSupplierStatus === "Acknowledged" ||
    supplier?.acceptTermsConditions === true;

  const handleRemoveSupplier = (associateNumber) => {
    setSupplierList((prev) =>
      prev.filter((s) => s.AssociateNumber !== associateNumber),
    );
  };

  const handleAddSuppliers = (rows) => {
    // Merge selected suppliers, avoiding duplicates by AssociateNumber
    setSupplierList((prev) => {
      const existing = new Set(prev.map((s) => s.AssociateNumber));
      const newSuppliers = rows
        .filter((r) => !existing.has(r.AssociateNumber))
        .map((r) => ({
          AssociateNumber: r.AssociateNumber,
          status: r.status,
          organisationName: r.organisationName,
          contactfirstName: r.contactfirstName,
          contactNumber: r.contactNumber,
          email: r.email,
          initiator: r.initiator,
          vendorCategory: r.vendorCategory,
          rfxSupplierStatus: r.rfxSupplierStatus || "",
          acceptTermsConditions: r.acceptTermsConditions || false,
          createdAt: r.createdAt,
          companyCode: r.companyCode,
        }));
      return [...prev, ...newSuppliers];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      businessComponentInstanceId: formInputs?.rfxNr,
      supplierList: supplierList.map((s) => ({
        AssociateNumber: s.AssociateNumber,
        status: s.status,
        organisationName: s.organisationName,
        contactfirstName: s.contactfirstName,
        contactNumber: s.contactNumber,
        email: s.email,
        initiator: s.initiator,
        vendorCategory: s.vendorCategory,
        rfxSupplierStatus: s.rfxSupplierStatus,
        acceptTermsConditions: s.acceptTermsConditions,
        createdAt: s.createdAt,
        companyCode: s.companyCode,
      })),
    };

    const toastId = toast.loading("Saving suppliers...", {
      position: "top-right",
    });
    try {
      const res = await ItemService.updateRfx(payload);
      if (res?.status === 200) {
        toast.success(res?.message || "Suppliers updated successfully", {
          position: "top-right",
        });
        onHide();
        if (onSuccess) onSuccess();
      } else {
        toast.error(res?.message || "Failed to update suppliers", {
          position: "top-right",
        });
      }
    } catch (err) {
      toast.error("Something went wrong", { position: "top-right" });
    }
    toast.dismiss(toastId);
    setSaving(false);
  };

  const columns = [
    {
      name: "Supplier ID",
      selector: (row) => row.AssociateNumber || "--",
      width: "120px",
    },
    {
      name: "Name",
      selector: (row) => row.organisationName || "--",
      width: "180px",
    },
    {
      name: "Contact",
      selector: (row) => row.contactfirstName || "--",
      width: "130px",
    },
    {
      name: "Email",
      selector: (row) => row.email || "--",
      width: "220px",
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={
            isAcknowledged(row)
              ? "text-success fw-bold"
              : "text-warning fw-bold"
          }
        >
          {isAcknowledged(row) ? "Acknowledged" : "Pending"}
        </span>
      ),
      width: "130px",
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          className="btn btn-sm btn-danger"
          disabled={isAcknowledged(row)}
          onClick={() => handleRemoveSupplier(row.AssociateNumber)}
          title={
            isAcknowledged(row)
              ? "Cannot remove - supplier already acknowledged"
              : "Remove supplier"
          }
        >
          <i className="bi bi-trash"></i>
        </button>
      ),
      width: "90px",
    },
  ];

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Manage Suppliers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col xs={12} md={12} lg={12}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">
                  Total Suppliers: {supplierList.length}
                </span>
              </div>
              <DataTable
                columns={columns}
                data={supplierList}
                pagination
                paginationRowsPerPageOptions={[5, 10, 20]}
                noDataComponent="No suppliers added yet"
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Row className="w-100">
            <Col lg={4}>
              <button className="btnTable btn-danger" onClick={onHide}>
                <i className="bi bi-x-circle me-1"></i> Cancel
              </button>
            </Col>
            <Col lg={4}>
              <button
                className="btnTable"
                onClick={() => setSupplierModal(true)}
              >
                <i className="bi bi-plus-circle me-1"></i> Add Supplier
              </button>
            </Col>
            <Col lg={4}>
              <button
                className="btnTable"
                onClick={handleSave}
                disabled={saving}
              >
                <i className="bi bi-check-circle me-1"></i>{" "}
                {saving ? "Saving..." : "Save"}
              </button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      <CreateRFQSuppliers
        show={getSupplierModal}
        onHide={() => {
          setSupplierModal(false);
          setSelectedSuppliers([]);
        }}
        selectedSuppliers={selectedSuppliers}
        onSelectedRows={setSelectedSuppliers}
        passDataToParent={handleAddSuppliers}
      />
    </>
  );
};

export default ManageSuppliersModal;
